import { ErrorDisplay } from 'Components/Display';
import React, { useEffect, useState } from 'react';
import './DateRangePicker.scss';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DateRangePicker } from '@mui/x-date-pickers-pro/DateRangePicker';
import { SingleInputDateRangeField } from '@mui/x-date-pickers-pro/SingleInputDateRangeField';
import dayjs from 'dayjs';
import { PickerRangeValue } from '@mui/x-date-pickers/internals';
import ArrowBackIosRoundedIcon from '@mui/icons-material/ArrowBackIosRounded';
import ArrowNextIosRoundedIcon from '@mui/icons-material/ArrowForwardIosRounded';
import ClearIcon from '@mui/icons-material/Clear';
import IconButton from '@mui/material/IconButton';

interface DateRangePickerInputProps {
  label?: string;
  startDate?: Date | null;
  endDate?: Date | null;
  valueString?: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  required?: boolean;
  error?: string;
  hint?: string;
  dateOnly?: boolean;
  isTable?: boolean;
  clearable?: boolean;
}

export function formatDate(date: Date) {
  const combined = new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    date.getHours(),
    date.getMinutes(),
    date.getSeconds()
  );
  return formatLocalDate(combined);
}

function formatLocalDate(date: Date) {
  const pad = (n: number) => n.toString().padStart(2, '0');
  return (
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
      date.getDate()
    )} ` +
    `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(
      date.getSeconds()
    )}`
  );
}

const MASK_USER_INPUT_SYMBOL = '_';
const MIN_VALID_YEAR = 1800;
const MAX_VALID_YEAR = 3000;

export const DateRangePickerInput = (props: DateRangePickerInputProps) => {
  const {
    label,
    valueString,
    startDate,
    endDate,
    onChange,
    required,
    error,
    hint,
    dateOnly,
    clearable,
  } = props;
  // For dateOnly, strip timezone by rebuilding from UTC date components so midnight-UTC
  // dates don't shift to the previous day in local timezones.
  const toDay = (date: Date) =>
    dateOnly
      ? dayjs(
          new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate())
        )
      : dayjs(date);

  const [dateValue, setDateValue] = useState<PickerRangeValue>(() => {
    if (startDate && endDate) {
      const start = toDay(startDate);
      const end = toDay(endDate);
      if (start.isValid() && end.isValid()) return [start, end];
    }
    return [null, null];
  });

  useEffect(() => {
    if (startDate && endDate) {
      const start = toDay(startDate);
      const end = toDay(endDate);
      if (start.isValid() && end.isValid()) {
        setDateValue([start, end]);
      }
    }
  }, [startDate, endDate]);

  useEffect(() => {
    if (valueString) {
      const idx = valueString.indexOf('|');
      if (idx === -1) return;
      // Strip any time/timezone suffix before parsing so UTC midnight strings like
      // "2024-01-15T00:00:00Z" don't shift to the previous day in non-UTC locales.
      const parsePart = (s: string) => dayjs(s.split('T')[0]);
      const start = parsePart(valueString.substring(0, idx));
      const end = parsePart(valueString.substring(idx + 1));
      if (start.isValid() && end.isValid()) {
        setDateValue([start, end]);
      }
    } else {
      setDateValue([null, null]);
    }
  }, [valueString, dateOnly]);

  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [hasInteracted, setHasInteracted] = useState(false);
  const [popperPlacement, setPopperPlacement] = useState<
    'bottom-start' | 'bottom-end'
  >('bottom-start');

  const dateValueRef = React.useRef<PickerRangeValue>([null, null]);
  const wrapperRef = React.useRef<HTMLDivElement>(null);

  const CALENDAR_WIDTH = 580; // approximate width of the 2-month calendar popup

  const updatePopperPlacement = () => {
    if (wrapperRef.current) {
      const rect = wrapperRef.current.getBoundingClientRect();
      setPopperPlacement(
        rect.left + CALENDAR_WIDTH > window.innerWidth
          ? 'bottom-end'
          : 'bottom-start'
      );
    }
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    setDateValue([null, null]);
    setHasInteracted(false);
    dateValueRef.current = [null, null];
    onChange({ target: { value: '' } } as React.ChangeEvent<HTMLInputElement>);
  };

  const handleFocus = () => setIsFocused(true);

  const onDateChange = (newValue: PickerRangeValue) => {
    dateValueRef.current = newValue;
    setDateValue(newValue);
    const [start, end] = newValue;
    if (
      start &&
      end &&
      start.isValid() &&
      end.isValid() &&
      start.year() > MIN_VALID_YEAR &&
      end.year() > MIN_VALID_YEAR &&
      start.year() < MAX_VALID_YEAR &&
      end.year() < MAX_VALID_YEAR
    ) {
      // Use a year-2000 proxy to get a clean DST-aware offset — avoids pre-1900
      // historical timezone anomalies (e.g. "UTC-6:02:04") that toISOString() applies.
      const toUtcMidnight = (d: typeof start) => {
        const proxy = new Date(2000, d.month(), d.date());
        const offsetMin = proxy.getTimezoneOffset();
        return d.add(offsetMin, 'minute').format('YYYY-MM-DDTHH:mm:ss') + 'Z';
      };
      const value = dateOnly
        ? `${start.format('MM/DD/YYYY')}|${end.format('MM/DD/YYYY')}`
        : `${toUtcMidnight(start)}|${toUtcMidnight(end)}`;
      onChange({
        target: { value },
      } as React.ChangeEvent<HTMLInputElement>);
    } else if (!start && !end) {
      onChange({
        target: { value: '' },
      } as React.ChangeEvent<HTMLInputElement>);
    }
  };

  return (
    <div className={`date-picker-wrapper${isPickerOpen ? ' picker-open' : ''}`}>
      {label && (
        <span
          className={
            isFocused ? 'date-picker-label-active' : 'date-picker-label'
          }
        >
          {label} {required && <span className="required">*</span>}
        </span>
      )}
      <div className="date-time-picker-row" onFocus={handleFocus}>
        <div className="date-range-input-row">
          <div ref={wrapperRef} className="date-range-picker-wrapper">
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DateRangePicker
                onChange={onDateChange}
                value={dateValue}
                onOpen={() => {
                  updatePopperPlacement();
                }}
                slots={{
                  field: SingleInputDateRangeField,
                  leftArrowIcon: ArrowBackIosRoundedIcon,
                  rightArrowIcon: ArrowNextIosRoundedIcon,
                }}
                localeText={{
                  fieldMonthPlaceholder: () => 'MM',
                  fieldDayPlaceholder: () => 'DD',
                  fieldYearPlaceholder: () => 'YYYY',
                }}
                slotProps={{
                  field: { dateSeparator: '-' } as any,
                  popper: {
                    placement: popperPlacement,
                    modifiers: [{ name: 'flip', enabled: false }],
                    container: document.body,
                    popperOptions: { strategy: 'fixed' },
                  },
                }}
              />
            </LocalizationProvider>
          </div>
          {clearable && (dateValue[0] || dateValue[1]) && (
            <IconButton
              size="small"
              onClick={handleClear}
              className="date-range-clear-btn"
              tabIndex={-1}
            >
              <ClearIcon fontSize="small" />
            </IconButton>
          )}
        </div>
        {hint && !error && <div className="hint">{hint}</div>}
        <ErrorDisplay errors={[error ?? '']} />
      </div>
    </div>
  );
};
